import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth as useClerkAuth, useUser as useClerkUser, useClerk as useClerkClerk } from '@clerk/clerk-react';

// Test mode flag - set REACT_APP_DISABLE_AUTH=true to bypass Clerk
export const isTestMode = process.env.REACT_APP_DISABLE_AUTH === 'true';

// Mock user data for testing
export const mockUser = {
  id: 'test_user_123',
  primaryEmailAddress: {
    emailAddress: 'test@claimsync.com'
  },
  fullName: 'Test User',
  firstName: 'Test',
  lastName: 'User',
  imageUrl: '',
  username: 'testuser',
  // Additional properties that Clerk user might have
  emailAddresses: [{ emailAddress: 'test@claimsync.com' }],
  publicMetadata: {},
  unsafeMetadata: {}
};

// Mock auth data for testing
const mockAuthData = {
  isLoaded: true,
  isSignedIn: true,
  userId: 'test_user_123',
  sessionId: 'test_session_123',
  getToken: async () => 'test_token_123',
  signOut: async () => { console.log('Test mode: signOut called'); }
};

// Mock clerk data for testing
const mockClerkData = {
  signOut: async () => { console.log('Test mode: signOut called'); },
  openSignIn: () => { console.log('Test mode: openSignIn called'); },
  openSignUp: () => { console.log('Test mode: openSignUp called'); }
};

interface TestAuthContextType {
  isTestMode: boolean;
  user: typeof mockUser | null;
  isLoaded: boolean;
  isSignedIn: boolean;
}

const TestAuthContext = createContext<TestAuthContextType>({
  isTestMode: false,
  user: null,
  isLoaded: true,
  isSignedIn: false
});

export const useTestAuth = () => useContext(TestAuthContext);

// Drop-in replacement hooks that work in both test and normal mode
// In test mode, return mock data immediately WITHOUT calling Clerk hooks
export function useUser() {
  // In test mode, return mock data immediately to avoid ClerkProvider errors
  if (isTestMode) {
    return {
      user: mockUser,
      isLoaded: true,
      isSignedIn: true
    };
  }
  
  // Only call Clerk hook in normal mode
  try {
    return useClerkUser();
  } catch {
    // Fallback if Clerk context not available
    return {
      user: mockUser,
      isLoaded: true,
      isSignedIn: true
    };
  }
}

export function useAuth() {
  // In test mode, return mock data immediately WITHOUT calling Clerk hooks
  // This prevents the "AuthContext not found" error when ClerkProvider is not present
  if (isTestMode) {
    return mockAuthData;
  }
  
  // Only call Clerk hook in normal mode
  try {
    return useClerkAuth();
  } catch {
    return mockAuthData;
  }
}

export function useClerk() {
  // In test mode, return mock data immediately to avoid ClerkProvider errors
  if (isTestMode) {
    return mockClerkData;
  }
  
  // Only call Clerk hook in normal mode
  try {
    return useClerkClerk();
  } catch {
    return mockClerkData;
  }
}

export function TestAuthProvider({ children }: { children: ReactNode }) {
  const value: TestAuthContextType = {
    isTestMode,
    user: isTestMode ? mockUser : null,
    isLoaded: true,
    isSignedIn: isTestMode
  };

  return (
    <TestAuthContext.Provider value={value}>
      {children}
    </TestAuthContext.Provider>
  );
}

// Helper component that renders children only when "signed in" (bypasses auth in test mode)
export function TestSignedIn({ children }: { children: ReactNode }) {
  if (isTestMode) {
    return <>{children}</>;
  }
  return null;
}

// Helper component that renders children only when "signed out" (never in test mode)
export function TestSignedOut({ children }: { children: ReactNode }) {
  if (isTestMode) {
    return null;
  }
  return <>{children}</>;
}
